// Configuration utility for Live Employee Status page
export interface LiveStatusConfig {
  searchTerm: string;
  selectedStatuses: string[];
  selectedCampaigns: string[];
  refreshInterval: number;
  showFilters: boolean;
  tvMode: boolean;
}

const DEFAULT_CONFIG: LiveStatusConfig = {
  searchTerm: '',
  selectedStatuses: [],
  selectedCampaigns: [],
  refreshInterval: 30000, // 30 seconds
  showFilters: true,
  tvMode: false,
};

const CONFIG_STORAGE_KEY = 'liveStatusConfig';

export const saveLiveStatusConfig = (config: LiveStatusConfig): void => {
  try {
    localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(config));
    console.log('✅ Live Status configuration saved:', config);
  } catch (error) {
    console.error('❌ Failed to save Live Status configuration:', error);
  }
};

export const loadLiveStatusConfig = (): LiveStatusConfig => {
  try {
    const saved = localStorage.getItem(CONFIG_STORAGE_KEY);
    if (saved) {
      const config = JSON.parse(saved);
      // Merge with default config to ensure all properties exist
      const mergedConfig = { ...DEFAULT_CONFIG, ...config };
      console.log('✅ Live Status configuration loaded:', mergedConfig);
      return mergedConfig;
    }
  } catch (error) {
    console.error('❌ Failed to load Live Status configuration:', error);
  }
  
  console.log('📋 Using default Live Status configuration');
  return DEFAULT_CONFIG;
};

export const resetLiveStatusConfig = (): LiveStatusConfig => {
  try {
    localStorage.removeItem(CONFIG_STORAGE_KEY);
    console.log('🔄 Live Status configuration reset to defaults');
  } catch (error) {
    console.error('❌ Failed to reset Live Status configuration:', error);
  }
  
  return DEFAULT_CONFIG;
};

export const exportLiveStatusConfig = (config: LiveStatusConfig): void => {
  try {
    const dataStr = JSON.stringify(config, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'live-status-config.json';
    link.click();
    URL.revokeObjectURL(url);
    console.log('📤 Live Status configuration exported');
  } catch (error) {
    console.error('❌ Failed to export Live Status configuration:', error);
  }
};

export const importLiveStatusConfig = (file: File): Promise<LiveStatusConfig> => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const config = JSON.parse(e.target?.result as string);
          const mergedConfig = { ...DEFAULT_CONFIG, ...config };
          console.log('📥 Live Status configuration imported:', mergedConfig);
          resolve(mergedConfig);
        } catch (parseError) {
          console.error('❌ Failed to parse imported configuration:', parseError);
          reject(new Error('Invalid configuration file format'));
        }
      };
      reader.onerror = () => {
        console.error('❌ Failed to read imported configuration file');
        reject(new Error('Failed to read configuration file'));
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('❌ Failed to import Live Status configuration:', error);
      reject(error);
    }
  });
};
